import type {
  BalanceSheetApiData,
  BalanceSheetApiResponse,
  CashFlowApiData,
  CashFlowApiResponse,
  FullYearForecastType,
  FullYearResultsApiData,
  FullYearResultsApiResponse,
  ResearchDevelopmentApiData,
  ResearchDevelopmentApiResponse,
} from '@app-types/performanceTrendsApi';
import type { InstrumentQueryParams } from '@utils/queryParams';
import { fundamentalsClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

/**
 * GET /api/fundamentals/balance-sheet?ticker=&mkt=
 * Returns one row per fiscal period for the 貸借対照表 section. The section
 * mapper determines chart and table display order independently.
 * Sends the auth token via the `x-access-token` header (fundamentalsClient
 * interceptor); the token is never placed in the query string or logged.
 */
export const fetchBalanceSheet = async (
  params: InstrumentQueryParams,
): Promise<BalanceSheetApiData> => {
  try {
    const response = await fundamentalsClient.get<BalanceSheetApiResponse>('/balance-sheet', {
      params: { ticker: params.ticker, mkt: params.mkt },
    });

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwDataApiError(
        response.data,
        response.status,
        'Balance sheet response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/fundamentals/cash-flow?ticker=&mkt=
 * Returns one row per fiscal period for the Cash Flow section. Sends the auth
 * token via the `x-access-token` header (fundamentalsClient interceptor); the
 * token is never placed in the query string or logged.
 */
export const fetchCashFlow = async (
  params: InstrumentQueryParams,
): Promise<CashFlowApiData> => {
  try {
    const response = await fundamentalsClient.get<CashFlowApiResponse>('/cash-flow', {
      params: { ticker: params.ticker, mkt: params.mkt },
    });

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwDataApiError(
        response.data,
        response.status,
        'Cash flow response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/fundamentals/research-development?ticker=&mkt=
 * Returns one row per fiscal period for the Research/Development section.
 * Auth stays in the `x-access-token` header via `fundamentalsClient`.
 */
export const fetchResearchDevelopment = async (
  params: InstrumentQueryParams,
): Promise<ResearchDevelopmentApiData> => {
  try {
    const response = await fundamentalsClient.get<ResearchDevelopmentApiResponse>(
      '/research-development',
      {
        params: { ticker: params.ticker, mkt: params.mkt },
      },
    );

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwDataApiError(
        response.data,
        response.status,
        'Research/development response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

/**
 * GET /api/fundamentals/full-year-results?ticker=&mkt=&forecastType=&years=
 * Returns actual (`STD`) and selected forecast rows for the é€šæœŸæ¥­ç¸¾æŽ¨ç§»
 * section. Auth stays in the `x-access-token` header via `fundamentalsClient`.
 */
export const fetchFullYearResults = async (
  params: InstrumentQueryParams & {
    forecastType: FullYearForecastType;
    years: number;
  },
): Promise<FullYearResultsApiData> => {
  try {
    const response = await fundamentalsClient.get<FullYearResultsApiResponse>(
      '/full-year-results',
      {
        params: {
          ticker: params.ticker,
          mkt: params.mkt,
          forecastType: params.forecastType,
          years: params.years,
        },
      },
    );

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwDataApiError(
        response.data,
        response.status,
        'Full-year results response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

type QuarterlyResultsForecastType =
  import('@app-types/performanceTrendsApi').QuarterlyResultsForecastType;
type QuarterlyResultsApiData =
  import('@app-types/performanceTrendsApi').QuarterlyResultsApiData;
type QuarterlyResultsApiResponse =
  import('@app-types/performanceTrendsApi').QuarterlyResultsApiResponse;

/**
 * GET /api/fundamentals/quarterly-results?ticker=&mkt=&forecastType=&years=
 * Returns actual (`STD`) and selected forecast rows for the quarterly section.
 * Auth stays in the `x-access-token` header via `fundamentalsClient`.
 */
export const fetchQuarterlyResults = async (
  params: InstrumentQueryParams & {
    forecastType: QuarterlyResultsForecastType;
    years: number;
  },
): Promise<QuarterlyResultsApiData> => {
  try {
    const response = await fundamentalsClient.get<QuarterlyResultsApiResponse>(
      '/quarterly-results',
      {
        params: {
          ticker: params.ticker,
          mkt: params.mkt,
          forecastType: params.forecastType,
          years: params.years,
        },
      },
    );

    if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
      throwDataApiError(
        response.data,
        response.status,
        'Quarterly results response is invalid.',
      );
    }

    return response.data.data;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};
